/**
 * Colour maths for the preview. The accent is the organisation's own brand
 * colour (`lookAndFeel.assetFrontendBgcolorHex`), so the readable foreground is
 * derived at runtime rather than assumed.
 */
type Rgb = { r: number; g: number; b: number }

const FALLBACK_ACCENT = '#002EFF'
const INK_ON_LIGHT = '#000000'
const INK_ON_DARK = '#FFFFFF'

export function normaliseHex(input: string | null | undefined): string {
  if (!input) return FALLBACK_ACCENT
  let h = input.trim().replace(/^#/, '')
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('')
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return FALLBACK_ACCENT
  return '#' + h.toUpperCase()
}

function hexToRgb(hex: string): Rgb {
  const h = normaliseHex(hex).slice(1)
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

function toHex({ r, g, b }: Rgb): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)))
  return (
    '#' +
    [clamp(r), clamp(g), clamp(b)]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
  )
}

/** Mix `amount` (0..1) of `targetHex` into `fromHex`. */
export function mix(fromHex: string, targetHex: string, amount: number): string {
  const a = hexToRgb(fromHex)
  const b = hexToRgb(targetHex)
  const t = Math.max(0, Math.min(1, amount))
  return toHex({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  })
}

function channel(v: number): number {
  const c = v / 255
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex)
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

function contrastRatio(a: number, b: number): number {
  const hi = Math.max(a, b)
  const lo = Math.min(a, b)
  return (hi + 0.05) / (lo + 0.05)
}

/** The ink with the higher contrast against an arbitrary accent. */
export function readableInk(hex: string): string {
  const lum = relativeLuminance(hex)
  const onLight = contrastRatio(lum, relativeLuminance(INK_ON_LIGHT))
  const onDark = contrastRatio(lum, relativeLuminance(INK_ON_DARK))
  return onDark >= onLight ? INK_ON_DARK : INK_ON_LIGHT
}

/**
 * A version of the accent that is legible as *text* on a light surface. A brand
 * colour can be arbitrarily light (an amber, a lime), so this darkens it toward
 * black until it clears 4.5:1 against the surface.
 */
export function readableAccentOn(accent: string, surface: string): string {
  const target = relativeLuminance(surface)
  let c = normaliseHex(accent)
  for (let i = 0; i < 12 && contrastRatio(relativeLuminance(c), target) < 4.5; i++) {
    c = mix(c, '#000000', 0.12)
  }
  return c
}
